import { courseBadgeForStep, courseForloebProgress } from '@/lib/courseAppointmentBadge';
import type { BookedStepStatus } from '@/types/treatmentCourses';

export const APPOINTMENT_DISPLAY_STATUSES = [
  'not_booked',
  'booked',
  'completed',
  'cancelled',
  'no_show',
] as const;

export type AppointmentDisplayStatus = (typeof APPOINTMENT_DISPLAY_STATUSES)[number];

export const QUESTIONNAIRE_DISPLAY_STATES = [
  'planned',
  'sent',
  'completed',
  'failed',
  'skipped',
] as const;

export type QuestionnaireDisplayState = (typeof QUESTIONNAIRE_DISPLAY_STATES)[number];

export const STEP_LANES = ['done', 'current', 'future'] as const;
export type StepLane = (typeof STEP_LANES)[number];

export const BOOKED_BY_CHANNELS = ['staff', 'patient_online'] as const;
export type BookedByChannel = (typeof BOOKED_BY_CHANNELS)[number];

export type CourseStepStatusInput = {
  id: string;
  status: BookedStepStatus;
  sort_order: number;
};

export const SENDING_JOB_STALE_MS = 15 * 60 * 1000;

export type QuestionnaireStateInput = {
  jobStatus?: string | null;
  scheduledFor?: string | null;
  attemptedAt?: string | null;
  jobReason?: string | null;
  assignmentStatus?: string | null;
  sentAt?: string | null;
  completedAt?: string | null;
};

export type QuestionnaireStateResult = {
  state: QuestionnaireDisplayState;
  at: string | null;
};

function asAppointmentDisplayStatus(value: string | null | undefined): AppointmentDisplayStatus | null {
  if (!value) return null;
  return APPOINTMENT_DISPLAY_STATUSES.includes(value as AppointmentDisplayStatus)
    ? (value as AppointmentDisplayStatus)
    : null;
}

/**
 * Staff-facing appointment status for a booked course step.
 * Prefers the linked appointment when present; otherwise the step row.
 */
export function deriveAppointmentDisplayStatus(input: {
  stepStatus: BookedStepStatus;
  appointmentId?: string | null;
  appointmentStatus?: string | null;
}): AppointmentDisplayStatus {
  const appointmentStatus = input.appointmentStatus?.trim() ?? '';
  if (appointmentStatus === 'completed') return 'completed';
  if (appointmentStatus === 'cancelled') return 'cancelled';
  if (appointmentStatus === 'no_show') return 'no_show';
  if (appointmentStatus === 'upcoming') return 'booked';

  switch (input.stepStatus) {
    case 'completed':
      return 'completed';
    case 'cancelled':
      return 'cancelled';
    case 'no_show':
      return 'no_show';
    case 'upcoming':
      return 'booked';
    case 'unscheduled':
      return input.appointmentId ? 'booked' : 'not_booked';
    default: {
      const _exhaustive: never = input.stepStatus;
      return _exhaustive;
    }
  }
}

/**
 * Questionnaire lifecycle for a course-step automation / pre-visit job.
 * A pending/sending job stays planlagt (Send nu still possible after a failed
 * send that rolled the job back). Assignment completion still wins.
 */
export function deriveQuestionnaireState(input: QuestionnaireStateInput): QuestionnaireStateResult {
  const assignment = input.assignmentStatus?.trim() ?? '';
  if (assignment === 'completed') {
    return { state: 'completed', at: input.completedAt ?? input.sentAt ?? null };
  }

  const job = input.jobStatus?.trim() ?? '';
  if (job === 'failed') {
    return { state: 'failed', at: input.attemptedAt ?? null };
  }
  if (job === 'skipped') {
    return { state: 'skipped', at: input.attemptedAt ?? null };
  }

  if (assignment === 'cancelled' || assignment === 'expired') {
    return { state: 'skipped', at: input.sentAt ?? input.attemptedAt ?? null };
  }

  if (job === 'pending' || job === 'sending') {
    return { state: 'planned', at: input.scheduledFor ?? null };
  }

  if (assignment === 'sent' || assignment === 'reminded' || job === 'sent') {
    return { state: 'sent', at: input.sentAt ?? input.attemptedAt ?? null };
  }

  return { state: 'planned', at: input.scheduledFor ?? null };
}

export function isStaleSendingJob(
  jobStatus?: string | null,
  attemptedAt?: string | null,
  nowMs: number = Date.now(),
): boolean {
  if (jobStatus?.trim() !== 'sending') return false;
  if (!attemptedAt) return true;
  const attempted = Date.parse(attemptedAt);
  if (Number.isNaN(attempted)) return true;
  return nowMs - attempted >= SENDING_JOB_STALE_MS;
}

export function canSendPlannedQuestionnaire(
  state: QuestionnaireDisplayState,
  jobStatus?: string | null,
  attemptedAt?: string | null,
  nowMs?: number,
): boolean {
  const job = jobStatus?.trim() ?? '';
  if (state === 'completed') return false;
  if (job === 'sent') return false;
  if (job === 'sending' && !isStaleSendingJob(job, attemptedAt, nowMs ?? Date.now())) {
    return false;
  }
  switch (state) {
    case 'planned':
      return true;
    case 'sent':
    case 'failed':
    case 'skipped':
      return false;
    default: {
      const _exhaustive: never = state;
      return _exhaustive;
    }
  }
}

export function parseClaimQuestionnaireSendResult(raw: unknown): {
  claimed: boolean;
  hadJob: boolean;
  status: string | null;
  jobId: string | null;
} {
  if (!raw || typeof raw !== 'object') {
    return { claimed: false, hadJob: false, status: null, jobId: null };
  }
  const row = raw as Record<string, unknown>;
  return {
    claimed: row.claimed === true,
    hadJob: row.had_job === true,
    status: typeof row.status === 'string' ? row.status : null,
    jobId: typeof row.job_id === 'string' ? row.job_id : null,
  };
}

/**
 * Current/next step: first non-cancelled upcoming or unscheduled step.
 * If none remain, the last completed step (course finished).
 */
export function deriveCurrentStepId(steps: readonly CourseStepStatusInput[]): string | null {
  const ordered = [...steps].sort((a, b) => a.sort_order - b.sort_order);
  const nextOpen = ordered.find((step) => step.status === 'upcoming' || step.status === 'unscheduled');
  if (nextOpen) return nextOpen.id;
  const completed = [...ordered].reverse().find((step) => step.status === 'completed');
  return completed?.id ?? null;
}

export function deriveStepLane(
  steps: readonly CourseStepStatusInput[],
  stepId: string,
): StepLane {
  const currentStepId = deriveCurrentStepId(steps);
  const step = steps.find((row) => row.id === stepId);
  if (!step) return 'future';
  if (step.status === 'completed' || step.status === 'no_show' || step.status === 'cancelled') {
    return 'done';
  }
  if (step.id === currentStepId) return 'current';
  const current = steps.find((row) => row.id === currentStepId);
  if (current && step.sort_order < current.sort_order) return 'done';
  return 'future';
}

export function deriveBookedByChannel(source: string | null | undefined): BookedByChannel | null {
  if (!source) return null;
  const normalized = source.trim().toLowerCase();
  if (normalized === 'portal' || normalized === 'online_booking' || normalized === 'patient') {
    return 'patient_online';
  }
  if (normalized === 'web' || normalized === 'staff' || normalized.length > 0) {
    return 'staff';
  }
  return null;
}

/** Shared Trin N af M — same definition as the calendar badge. */
export function courseProgressLabel(steps: readonly CourseStepStatusInput[]): {
  n: number;
  total: number;
  completed: number;
} {
  return courseForloebProgress(steps);
}

export const COURSE_STEP_VISUALS = ['done', 'next', 'planned'] as const;
export type CourseStepVisual = (typeof COURSE_STEP_VISUALS)[number];

export function deriveCourseStepVisual(
  steps: readonly CourseStepStatusInput[],
  stepId: string,
): CourseStepVisual {
  const lane = deriveStepLane(steps, stepId);
  switch (lane) {
    case 'done':
      return 'done';
    case 'current':
      return 'next';
    case 'future':
      return 'planned';
    default: {
      const _exhaustive: never = lane;
      return _exhaustive;
    }
  }
}

export function stepCourseBadge(
  sortOrder: number,
  total: number,
): { n: number; total: number } | null {
  const badge = courseBadgeForStep(sortOrder, total);
  return badge ? { n: badge.index, total: badge.total } : null;
}

export function appointmentDisplayTone(
  status: AppointmentDisplayStatus,
): 'draft' | 'upcoming' | 'completed' | 'cancelled' | 'no_show' {
  switch (status) {
    case 'not_booked':
      return 'draft';
    case 'booked':
      return 'upcoming';
    case 'completed':
      return 'completed';
    case 'cancelled':
      return 'cancelled';
    case 'no_show':
      return 'no_show';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export function questionnaireDisplayTone(
  state: QuestionnaireDisplayState,
): 'draft' | 'upcoming' | 'completed' | 'cancelled' | 'red' {
  switch (state) {
    case 'planned':
      return 'draft';
    case 'sent':
      return 'upcoming';
    case 'completed':
      return 'completed';
    case 'skipped':
      return 'cancelled';
    case 'failed':
      return 'red';
    default: {
      const _exhaustive: never = state;
      return _exhaustive;
    }
  }
}

export function asAppointmentDisplayStatusFromUnknown(
  value: unknown,
): AppointmentDisplayStatus | null {
  return typeof value === 'string' ? asAppointmentDisplayStatus(value) : null;
}
